import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    Button,
    List,
    ListItem,
    Avatar,
    Paper,
    Divider,
    Alert,
    CircularProgress,
    Tooltip,
    Badge,
    Chip,
    Card,
    CardContent
} from '@mui/material';
import {
    Close as CloseIcon,
    Send as SendIcon,
    Message as MessageIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    AutoAwesome as BotIcon,
    Refresh as RefreshIcon,
    VideoCall as VideoCallIcon,
    FiberManualRecord as RecordIcon,
    Stop as StopIcon,
    Summarize as SummarizeIcon,
    Expand as ExpandIcon,
    Compress as CompressIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import axios from 'axios';

const VideoCallSlideOutPanel = ({
    open,
    onClose,
    phoneNumber,
    videoRoom,
    callStatus,
    onExpandVideo,
    isVideoExpanded = false
}) => {
    const [conversation, setConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingDuration, setRecordingDuration] = useState(0);
    const [recordingSid, setRecordingSid] = useState(null);
    const [meetingSummary, setMeetingSummary] = useState(null);
    const [generatingSummary, setGeneratingSummary] = useState(false);
    
    const messagesEndRef = useRef(null);
    const recordingTimerRef = useRef(null);

    const API_BASE = '/api/twilio';

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Phone number validation helper
    const isValidPhoneNumber = (phone) => {
        if (!phone) return false;
        
        // Remove any non-digit characters for length check
        const digitsOnly = phone.replace(/\D/g, '');
        
        // Valid Australian phone numbers:
        // +61419729517 (11 digits total: 61 + 9 digits)
        // 0419729517 (10 digits: 0 + 9 digits)
        // 419729517 (9 digits, will be prefixed with +61)
        if (phone.startsWith('+61')) {
            return digitsOnly.length >= 11; // 61 + 9 digits = 11 total
        } else if (phone.startsWith('0')) {
            return digitsOnly.length >= 10; // 0 + 9 digits = 10 total
        } else {
            return digitsOnly.length >= 9; // 9 digits, will be prefixed with +61
        }
    };

    // Initialize conversation when panel opens with a valid phone number
    useEffect(() => {
        // Debounce the phone number validation to avoid excessive API calls
        const timeoutId = setTimeout(() => {
            if (open && phoneNumber && !conversation && isValidPhoneNumber(phoneNumber)) {
                console.log('Phone number is valid, initializing conversation:', phoneNumber);
                initializeConversation();
            } else if (open && phoneNumber && !isValidPhoneNumber(phoneNumber)) {
                console.log('Phone number is incomplete, waiting for more digits:', phoneNumber);
            }
        }, 500); // Wait 500ms after user stops typing

        return () => clearTimeout(timeoutId);
    }, [open, phoneNumber]);

    // Poll for new messages while panel is open (faster polling for real-time feel)
    useEffect(() => {
        let interval;
        if (open && conversation) {
            interval = setInterval(() => {
                fetchMessages();
            }, 1000); // Poll every 1 second for real-time feel
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [open, conversation]);

    // Recording timer
    useEffect(() => {
        if (isRecording) {
            recordingTimerRef.current = setInterval(() => {
                setRecordingDuration(prev => prev + 1);
            }, 1000);
        } else {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
                recordingTimerRef.current = null;
            }
            setRecordingDuration(0);
        }

        return () => {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }
        };
    }, [isRecording]);

    const initializeConversation = async () => {
        if (!phoneNumber) return;

        setLoading(true);
        setError(null);

        try {
            // Normalize phone number
            let normalizedPhone = phoneNumber;
            if (normalizedPhone.startsWith('0')) {
                normalizedPhone = '+61' + normalizedPhone.slice(1);
            } else if (!normalizedPhone.startsWith('+')) {
                normalizedPhone = '+61' + normalizedPhone;
            }

            console.log('Initializing conversation for:', normalizedPhone);

            // First, try to find existing conversation
            const conversationsResponse = await axios.get(`${API_BASE}/conversations`);
            const existingConversation = conversationsResponse.data.find(conv =>
                getParticipantPhoneNumber(conv) === normalizedPhone
            );

            if (existingConversation) {
                console.log('Found existing conversation:', existingConversation.sid);
                setConversation(existingConversation);
                await fetchMessages(existingConversation.sid);
            } else {
                // Create new conversation
                console.log('Creating new conversation for:', normalizedPhone);
                const createResponse = await axios.post(`${API_BASE}/conversations`, {
                    participantNumber: normalizedPhone,
                    friendlyName: `Video Call Chat - ${normalizedPhone}`
                });
                
                // Backend now returns conversation object directly
                setConversation(createResponse.data);
                setMessages([]);
                console.log('Created new conversation:', createResponse.data.sid);
            }
        } catch (error) {
            console.error('Error initializing conversation:', error);
            setError(error.response?.data?.message || error.message || 'Failed to initialize conversation');
        } finally {
            setLoading(false);
        }
    };

    const fetchMessages = async (conversationSid = null) => {
        if (!conversationSid && !conversation) return;

        try {
            const sid = conversationSid || conversation.sid;
            const response = await axios.get(`${API_BASE}/conversations/${sid}/messages`);
            
            if (response.data.success) {
                const newMessages = response.data.messages;
                setMessages(prevMessages => {
                    // Check if there are new messages
                    const prevCount = prevMessages.length;
                    const newCount = newMessages.length;
                    
                    if (newCount > prevCount) {
                        setUnreadCount(prev => prev + (newCount - prevCount));
                    }
                    
                    return newMessages;
                });
                setError(null);
            } else {
                console.error('Failed to fetch messages:', response.data.message);
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
            // Don't set error for polling failures to avoid UI spam
        }
    };

    const sendMessage = async () => {
        if (!newMessage.trim() || !conversation || sending) return;

        setSending(true);
        try {
            const response = await axios.post(`${API_BASE}/conversations/${conversation.sid}/messages`, {
                body: newMessage.trim()
            });

            if (response.data.success) {
                setNewMessage('');
                // Fetch updated messages
                await fetchMessages();
                setError(null);
            } else {
                throw new Error(response.data.message || 'Failed to send message');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            setError(error.response?.data?.message || error.message || 'Failed to send message');
        } finally {
            setSending(false);
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            sendMessage();
        }
    };

    const getParticipantPhoneNumber = (conversation) => {
        if (!conversation || !conversation.participants) return '';
        
        // Look for participants with messaging binding (SMS participants)
        const phoneParticipant = conversation.participants.find(p => 
            p.messagingBinding && p.messagingBinding.address
        );
        
        if (phoneParticipant) {
            return phoneParticipant.messagingBinding.address;
        }
        
        // Fallback: look for identity-based participants (older format)
        const identityParticipant = conversation.participants.find(p => 
            p.identity && p.identity !== 'system' && p.identity.startsWith('+')
        );
        
        return identityParticipant ? identityParticipant.identity : '';
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const startRecording = async () => {
        try {
            if (!videoRoom) {
                throw new Error('No active video call to record');
            }

            // Call backend to start recording
            const response = await axios.post(`${API_BASE}/recordings/start`, {
                roomSid: videoRoom.sid || videoRoom.name
            });

            if (response.data.success) {
                setIsRecording(true);
                setRecordingSid(response.data.recordingSid);
                console.log('Recording started:', response.data.recordingSid);
            } else {
                throw new Error(response.data.message || 'Failed to start recording');
            }
        } catch (error) {
            console.error('Error starting recording:', error);
            setError(error.response?.data?.message || error.message || 'Failed to start recording');
        }
    };

    const stopRecording = async () => {
        try {
            // Call backend to stop recording using either recordingSid or roomSid
            const response = await axios.post(`${API_BASE}/recordings/stop`, {
                roomSid: videoRoom.sid || videoRoom.name,
                recordingSid: recordingSid
            });

            if (response.data.success) {
                setIsRecording(false);
                setRecordingSid(null);
                console.log('Recording stopped');
                
                // Optionally generate summary after recording stops
                setTimeout(() => {
                    generateMeetingSummary();
                }, 2000);
            } else {
                throw new Error(response.data.message || 'Failed to stop recording');
            }
        } catch (error) {
            console.error('Error stopping recording:', error);
            setError(error.response?.data?.message || error.message || 'Failed to stop recording');
        }
    };

    const generateMeetingSummary = async () => {
        if (!videoRoom) return;

        setGeneratingSummary(true);
        try {
            const response = await axios.post(`${API_BASE}/meetings/summary`, {
                roomSid: videoRoom.sid || videoRoom.name,
                phoneNumber: phoneNumber
            });

            if (response.data.success) {
                setMeetingSummary(response.data.summary);
                console.log('Meeting summary generated');
            } else {
                throw new Error(response.data.message || 'Failed to generate summary');
            }
        } catch (error) {
            console.error('Error generating meeting summary:', error);
            setError(error.response?.data?.message || error.message || 'Failed to generate meeting summary');
        } finally {
            setGeneratingSummary(false);
        }
    };

    const clearUnreadCount = () => {
        setUnreadCount(0);
    };

    // Clear unread count when panel opens
    useEffect(() => {
        if (open) {
            const timeout = setTimeout(clearUnreadCount, 1000);
            return () => clearTimeout(timeout);
        }
    }, [open]);

    return (
        <Box
            sx={{
                position: 'absolute',
                left: open ? '100%' : '0%',
                top: 0,
                width: open ? 400 : 0,
                height: '100%',
                backgroundColor: '#1a1a1a', // Match dialer background
                borderLeft: open ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                borderRadius: '0 12px 12px 0',
                transition: 'all 0.3s ease-in-out',
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: open ? '4px 0 20px rgba(0, 0, 0, 0.3)' : 'none',
                overflow: 'hidden'
            }}
        >
            {/* Header */}
            <Box sx={{
                p: 2,
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#1a1a1a' // Match dialer header
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VideoCallIcon sx={{ color: '#2196f3' }} />
                    <Box>
                        <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
                            Video Call Chat
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ 
                                color: phoneNumber && !isValidPhoneNumber(phoneNumber) ? '#ff6b6b' : '#888' 
                            }}>
                                {phoneNumber || 'No number'}
                            </Typography>
                            {phoneNumber && !isValidPhoneNumber(phoneNumber) && (
                                <Chip
                                    label="Invalid"
                                    size="small"
                                    sx={{
                                        height: 16,
                                        fontSize: '0.65rem',
                                        backgroundColor: 'rgba(255, 107, 107, 0.2)',
                                        color: '#ff6b6b',
                                        '& .MuiChip-label': { px: 1 }
                                    }}
                                />
                            )}
                            {phoneNumber && isValidPhoneNumber(phoneNumber) && conversation && (
                                <Chip
                                    label="Connected"
                                    size="small"
                                    sx={{
                                        height: 16,
                                        fontSize: '0.65rem',
                                        backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                        color: '#4caf50',
                                        '& .MuiChip-label': { px: 1 }
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                    {unreadCount > 0 && (
                        <Badge badgeContent={unreadCount} color="error" sx={{ ml: 1 }} />
                    )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={isVideoExpanded ? "Minimize video" : "Expand video"}>
                        <IconButton
                            onClick={onExpandVideo}
                            sx={{
                                color: '#ffffff',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                '&:hover': { 
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)'
                                }
                            }}
                        >
                            {isVideoExpanded ? <CompressIcon /> : <ExpandIcon />}
                        </IconButton>
                    </Tooltip>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: '#ffffff',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Recording Controls */}
            {videoRoom && (
                <Box sx={{
                    p: 1.5,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    backgroundColor: '#1a1a1a' // Match dialer background
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {isRecording ? (
                                <>
                                    <RecordIcon sx={{ color: '#f44336', fontSize: 16 }} />
                                    <Typography variant="body2" sx={{ color: '#f44336', fontWeight: 600 }}>
                                        REC {formatTime(recordingDuration)}
                                    </Typography>
                                </>
                            ) : (
                                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                                    Ready to record
                                </Typography>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                size="small"
                                variant={isRecording ? "outlined" : "contained"}
                                onClick={isRecording ? stopRecording : startRecording}
                                startIcon={isRecording ? <StopIcon /> : <RecordIcon />}
                                sx={{ 
                                    minWidth: 100,
                                    backgroundColor: isRecording ? 'transparent' : '#f44336',
                                    borderColor: isRecording ? '#f44336' : 'transparent',
                                    color: isRecording ? '#f44336' : '#ffffff',
                                    '&:hover': {
                                        backgroundColor: isRecording ? 'rgba(244, 67, 54, 0.1)' : '#d32f2f',
                                        borderColor: '#f44336'
                                    }
                                }}
                            >
                                {isRecording ? 'Stop' : 'Record'}
                            </Button>

                            <Button
                                size="small"
                                variant="outlined"
                                onClick={generateMeetingSummary}
                                disabled={generatingSummary || !videoRoom}
                                startIcon={generatingSummary ? <CircularProgress size={16} /> : <SummarizeIcon />}
                                sx={{
                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                    color: '#ffffff',
                                    '&:hover': {
                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                        backgroundColor: 'rgba(255, 255, 255, 0.05)'
                                    },
                                    '&:disabled': {
                                        borderColor: 'rgba(255, 255, 255, 0.1)',
                                        color: '#888'
                                    }
                                }}
                            >
                                Summary
                            </Button>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Meeting Summary */}
            {meetingSummary && (
                <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)', backgroundColor: '#1a1a1a' }}>
                    <Card sx={{ backgroundColor: 'rgba(33, 150, 243, 0.1)', border: '1px solid rgba(33, 150, 243, 0.3)' }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="subtitle2" sx={{ color: '#2196f3', mb: 1, fontWeight: 600 }}>
                                Meeting Summary
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#ffffff', lineHeight: 1.5 }}>
                                {meetingSummary}
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>
            )}

            {/* Messages Container */}
            <Box sx={{ 
                flex: 1, 
                overflow: 'hidden', 
                display: 'flex', 
                flexDirection: 'column',
                backgroundColor: '#000000' // Black background for conversation area
            }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                        <CircularProgress sx={{ color: '#2196f3' }} />
                    </Box>
                ) : error ? (
                    <Box sx={{ p: 2, backgroundColor: '#000000' }}>
                        <Alert severity="error" sx={{ backgroundColor: 'rgba(244, 67, 54, 0.1)' }}>
                            {error}
                            <Button
                                size="small"
                                onClick={initializeConversation}
                                sx={{ mt: 1, color: '#f44336' }}
                            >
                                Retry
                            </Button>
                        </Alert>
                    </Box>
                ) : phoneNumber && !isValidPhoneNumber(phoneNumber) ? (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        flex: 1,
                        p: 3,
                        backgroundColor: '#000000'
                    }}>
                        <PhoneIcon sx={{ fontSize: 48, color: '#ff6b6b', mb: 2 }} />
                        <Typography variant="h6" sx={{ color: '#ffffff', mb: 1, textAlign: 'center' }}>
                            Invalid Phone Number
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#888', textAlign: 'center', mb: 2 }}>
                            Please enter a complete phone number to start a conversation.
                            <br />
                            Format: +61412345678 or 0412345678
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#ff6b6b', textAlign: 'center' }}>
                            Current: {phoneNumber}
                        </Typography>
                    </Box>
                ) : !phoneNumber ? (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        flex: 1,
                        p: 3,
                        backgroundColor: '#000000'
                    }}>
                        <MessageIcon sx={{ fontSize: 48, color: '#888', mb: 2 }} />
                        <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
                            No Conversation
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#888', textAlign: 'center' }}>
                            Enter a phone number in the dialer to start a video call conversation.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ flex: 1, overflow: 'auto', p: 1, backgroundColor: '#000000' }}>
                        <List sx={{ p: 0 }}>
                            {messages.map((message, index) => {
                                // Determine if this is an incoming SMS (from phone number) or outgoing (from system)
                                const isIncomingSMS = message.author && message.author.startsWith('+');
                                const isOutgoing = !isIncomingSMS || message.author === 'system';
                                
                                return (
                                    <ListItem
                                        key={message.sid || index}
                                        sx={{
                                            flexDirection: 'column',
                                            alignItems: isOutgoing ? 'flex-end' : 'flex-start',
                                            mb: 1,
                                            p: 0
                                        }}
                                    >
                                        <Paper
                                            sx={{
                                                p: 1.5,
                                                maxWidth: '85%',
                                                backgroundColor: isOutgoing 
                                                    ? 'rgba(33, 150, 243, 0.2)' // Blue for outgoing
                                                    : 'rgba(76, 175, 80, 0.2)', // Green for incoming SMS
                                                color: '#ffffff',
                                                borderRadius: isOutgoing ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                                                border: isIncomingSMS ? '1px solid rgba(76, 175, 80, 0.3)' : 'none'
                                            }}
                                        >
                                            {isIncomingSMS && (
                                                <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                        color: '#4caf50', 
                                                        fontWeight: 600,
                                                        display: 'block',
                                                        mb: 0.5
                                                    }}
                                                >
                                                    📱 SMS Reply
                                                </Typography>
                                            )}
                                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                                {message.body}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: '#888',
                                                    display: 'block',
                                                    mt: 0.5,
                                                    textAlign: isOutgoing ? 'right' : 'left'
                                                }}
                                            >
                                                {format(new Date(message.dateCreated), 'HH:mm')}
                                                {isOutgoing && ' ✓'}
                                            </Typography>
                                        </Paper>
                                    </ListItem>
                                );
                            })}
                        </List>
                        <div ref={messagesEndRef} />
                    </Box>
                )}
            </Box>

            {/* Message Input */}
            <Box sx={{
                p: 2,
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                backgroundColor: '#1a1a1a' // Match dialer background
            }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        fullWidth
                        multiline
                        maxRows={3}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type your message..."
                        disabled={!conversation || sending}
                        size="small"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: '#ffffff',
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                '&.Mui-focused fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' }
                            },
                            '& .MuiInputBase-input::placeholder': { color: 'rgba(255, 255, 255, 0.5)' }
                        }}
                    />
                    <IconButton
                        onClick={sendMessage}
                        disabled={!newMessage.trim() || !conversation || sending}
                        sx={{
                            backgroundColor: '#2196f3',
                            color: 'white',
                            '&:hover': { 
                                backgroundColor: '#1976d2'
                            },
                            '&:disabled': { 
                                backgroundColor: '#424242', 
                                color: '#888' 
                            }
                        }}
                    >
                        {sending ? <CircularProgress size={20} /> : <SendIcon />}
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
};

export default VideoCallSlideOutPanel;
